import cx from 'clsx';
import React, { FunctionComponent, MouseEvent, useEffect, useState } from 'react';
import styles from './HeaderBottomItem.module.scss';
import { HeaderBottomNav } from 'components/presentional';
import { Icon, LinkWrapper, Typography } from 'components/elements';
import { OnTabletWithHoverOnly, OnTabletWithoutHoverOnly } from 'utils/breakpoints';
import { useClickOutsideRef } from 'utils/refs';
import { WPMenuItem } from 'types/menus';
import { ChevronDownSmall as ChevronDownSmallIcon } from 'assets/icons';
import utilityStyles from 'styles/global/utility.module.scss';
import { useLocation } from 'react-router-dom';
import { useCurrentWidth } from 'hooks/dom';
import { createMenuLink } from 'utils/links';

interface OwnProps {
  item: WPMenuItem;
  onlyIcon?: boolean;
}

type HeaderBottomItemProps = JSX.IntrinsicElements['div'] & OwnProps;

const highlightedSlugs = ['kontakt', 'contact'];

const HeaderBottomItem: FunctionComponent<HeaderBottomItemProps> = props => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const { ref: coutsideRef } = useClickOutsideRef<HTMLDivElement>(() => {
    setIsVisible(false);
  });
  const { item } = props;
  const isLink = item.url !== '#';
  const hasKids = item.child_items && item.child_items.length > 0;
  const windowWidth = useCurrentWidth();

  useEffect(() => {
    setIsVisible(false);
  }, [windowWidth]);

  const handleSubmenu = (isVisibleLocal: boolean) => (event: MouseEvent<HTMLDivElement>) => {
    if (isVisible !== isVisibleLocal) {
      setIsVisible(isVisibleLocal);
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.body.classList.add('noScroll');
    } else {
      document.body.classList.remove('noScroll');
    }
  }, [isVisible]);

  const submenuClassNames = cx({
    [styles.submenu]: true,
    [utilityStyles.elevation1]: true,
    [styles.visible]: isVisible,
  });

  const linkClassNames = cx({
    [styles.link]: isLink,
    [styles.notLink]: !isLink || hasKids,
    [styles.active]: location.pathname?.includes(`/${item.slug}`),
    [styles.highlighted]: highlightedSlugs.includes(item?.slug || ''),
  });

  return (
    <div
      ref={coutsideRef}
      key={item.ID}
      className={styles.item}
      {...(hasKids && {
        onMouseEnter: handleSubmenu(true),
        onMouseLeave: handleSubmenu(false),
      })}
    >
      <OnTabletWithHoverOnly>
        {isLink && (
          <LinkWrapper className={linkClassNames} href={createMenuLink(item)}>
            <Typography mobile="new_neue_16" tablet="new_neue_16" desktop="new_neue_16" as="span">
              {item.title}
            </Typography>
            {hasKids && <Icon size={20} icon={ChevronDownSmallIcon} />}
          </LinkWrapper>
        )}
        {!isLink && (
          <div className={linkClassNames}>
            <Typography mobile="new_neue_16" tablet="new_neue_16" desktop="new_neue_16" as="span">
              {item.title}
            </Typography>
            {hasKids && <Icon size={20} icon={ChevronDownSmallIcon} />}
          </div>
        )}
      </OnTabletWithHoverOnly>
      <OnTabletWithoutHoverOnly>
        {hasKids && (
          <div className={linkClassNames} onClick={handleSubmenu(!isVisible)}>
            <Typography mobile="new_neue_16" tablet="new_neue_16" desktop="new_neue_16" as="span">
              {item.title}
            </Typography>
            <Icon size={20} icon={ChevronDownSmallIcon} />
          </div>
        )}
        {!hasKids && isLink && (
          <LinkWrapper className={linkClassNames} href={createMenuLink(item)}>
            <Typography mobile="new_neue_16" tablet="new_neue_16" desktop="new_neue_16" as="span">
              {item.title}
            </Typography>
          </LinkWrapper>
        )}
        {!hasKids && !isLink && (
          <div className={linkClassNames}>
            <Typography mobile="new_neue_16" tablet="new_neue_16" desktop="new_neue_16" as="span">
              {item.title}
            </Typography>
          </div>
        )}
      </OnTabletWithoutHoverOnly>
      {item.child_items && item.child_items.length > 0 && (
        <HeaderBottomNav parent={item.slug} onClose={() => setIsVisible(false)} className={submenuClassNames} visible={isVisible} items={item.child_items} />
      )}
    </div>
  );
};

export default HeaderBottomItem;
